// Week-boundary bar-segment splitting for CalendarView's multi-day task bars
// (#42). Pure function, decoupled from Task/Date/JSX so it's directly
// testable: input is an ISO date range plus the visible grid's week starts,
// output is one segment per week the range overlaps, clipped at the grid's
// edges instead of dropped.
//
// ISO "YYYY-MM-DD" strings compare lexicographically the same as
// chronologically, so all containment/clipping below is plain string
// comparison — no Date object math, no UTC round-trips. addDaysIso/
// diffDaysIso (below) are the one place actual day arithmetic happens, and
// they're the same local-time helpers CalendarView already uses for its
// #876 drag & drop date math.

import { addDaysIso, diffDaysIso } from "./format";

export interface CalendarBarSegment {
  /** Index into the `weekStartIsos` grid this segment belongs to. */
  weekIndex: number;
  /** 0 (Monday) .. 6 (Sunday) — first column this segment occupies. */
  startCol: number;
  /** Number of columns this segment spans, 1..7. */
  span: number;
  /**
   * False when the segment's left edge is a week-boundary clip rather than
   * the task's actual start date — i.e. the bar continues from the
   * previous week's segment. Callers use this to flatten that edge instead
   * of rounding it, so a continuation reads as one bar, not two.
   */
  isStart: boolean;
  /** Same as `isStart`, for the segment's right edge / the next week. */
  isEnd: boolean;
}

/**
 * Split a task's [startIso, endIso] range into per-week bar segments against
 * a Monday-first week grid. `weekStartIsos[i]` is the Monday ISO date of
 * week `i`; every week is exactly 7 consecutive days.
 *
 * - `endIso` missing, or not after `startIso`, is treated as a one-day task
 *   on `startIso` — we never guess a duration that isn't there.
 * - The range is clipped to the grid at both ends rather than omitted: a
 *   task starting before the grid's first week (or ending after its last)
 *   still produces a segment for every week it overlaps, flush with that
 *   week's edge.
 */
export function splitTaskIntoWeekSegments(
  startIso: string,
  endIso: string | null,
  weekStartIsos: readonly string[]
): CalendarBarSegment[] {
  const effectiveEnd = endIso && endIso > startIso ? endIso : startIso;
  const segments: CalendarBarSegment[] = [];

  for (let weekIndex = 0; weekIndex < weekStartIsos.length; weekIndex++) {
    const weekStart = weekStartIsos[weekIndex];
    const weekEnd = addDaysIso(weekStart, 6);
    if (effectiveEnd < weekStart || startIso > weekEnd) continue;

    const segStart = startIso > weekStart ? startIso : weekStart;
    const segEnd = effectiveEnd < weekEnd ? effectiveEnd : weekEnd;
    const startCol = diffDaysIso(weekStart, segStart);
    const span = diffDaysIso(segStart, segEnd) + 1;
    if (startCol < 0 || startCol > 6 || span <= 0) continue;

    segments.push({
      weekIndex,
      startCol,
      span,
      isStart: segStart === startIso,
      isEnd: segEnd === effectiveEnd,
    });
  }

  return segments;
}
